/* eslint-disable no-restricted-imports */
import { createAction } from '@reduxjs/toolkit';
import { NavigateFunction } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { toMetricsUI } from '../../mappers/direct/toMetricsUI';
import { DirectProcessText } from '../../ui/business/ContentPromotion/types';
import { showNotificationAsync } from '../notifications/actions';
import { RootState } from '../store';
import { actionStatusMap, notificationStatusMap } from './constants';
import {
    selectDirectCampaigns,
    selectDirectCampaignSaving,
    selectDirectCampaignsLoading,
    selectDirectLoginsLoading,
} from './selectors';
import * as directService from './service';
import { AxiosError, DirectCampaignModel, DirectMetricsUI, DirectPromotionUI } from './types';

function showDirectErrorsAsync(
    error: AxiosError,
    defaultMessage: string,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        const errors = error.response?.data?.errors;

        if (!errors) {
            dispatch(showNotificationAsync(defaultMessage));

            return;
        }

        errors.forEach((e) => dispatch(showNotificationAsync(e.message)));
    };
}

export const setDirectMetrics = createAction(
    '@@direct/SET_DIRECT_METRICS',
    (payload: DirectMetricsUI) => ({ payload }),
);

export const setDirectCampaigns = createAction(
    '@@direct/SET_DIRECT_CAMPAIGNS',
    (payload: DirectCampaignModel[]) => ({ payload }),
);

export const setDirectCampaignsLoading = createAction<boolean>(
    '@@direct/SET_DIRECT_CAMPAIGNS_LOADING',
);

export const setDirectCampaignSaving = createAction<boolean>('@@direct/SET_DIRECT_CAMPAIGN_SAVING');

export const setDirectLogins = createAction('@@direct/SET_DIRECT_LOGINS', (payload: string[]) => ({
    payload,
}));

export const setDirectLoginsLoading = createAction<boolean>('@@direct/SET_DIRECT_LOGINS_LOADING');

export function loadDirectMetricsAsync(): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        try {
            const response = await directService.loadDirectMetrics();

            if (response.counters.length) {
                dispatch(setDirectMetrics(response));

                return;
            }

            const state = getState();
            const companyCounters = selectDirectCampaigns(state)?.[0]?.counters;

            if (companyCounters?.length) {
                dispatch(setDirectMetrics(toMetricsUI(companyCounters)));
            }
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось загрузить метрики'));
        }
    };
}

export function loadDirectCampaignsAsync(
    businessId: string,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const state = getState();
        const isLoading = selectDirectCampaignsLoading(state);

        if (isLoading) {
            return;
        }

        dispatch(setDirectCampaignsLoading(true));

        try {
            const campaigns = await directService.loadDirectCampaigns(businessId);
            dispatch(setDirectCampaigns(campaigns));
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось загрузить кампании'));
            dispatch(setDirectCampaigns([]));
        }

        dispatch(setDirectCampaignsLoading(false));
    };
}

export function saveDirectPromotionAsync(
    data: DirectPromotionUI,
    redirectUrl: string,
    navigate: NavigateFunction,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const state = getState();
        const isSaving = selectDirectCampaignSaving(state);

        if (isSaving) {
            return;
        }

        try {
            await dispatch(setDirectCampaignSaving(true));

            await directService.saveDirectPromotion(data);

            navigate(redirectUrl);
        } catch (error) {
            dispatch(
                showDirectErrorsAsync(
                    error as AxiosError,
                    'Не удалось отправить данные при создании кампании',
                ),
            );
        } finally {
            await dispatch(setDirectCampaignSaving(false));
        }
    };
}

export function editDirectPromotionAsync(
    data: DirectPromotionUI,
    companyId: string,
    redirectUrl: string,
    navigate: NavigateFunction,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const state = getState();
        const isSaving = selectDirectCampaignSaving(state);

        if (isSaving) {
            return;
        }

        try {
            await dispatch(setDirectCampaignSaving(true));

            await directService.editDirectPromotion(companyId, data);

            await dispatch(loadDirectCampaignsAsync(data.businessId));

            navigate(redirectUrl);
        } catch (error) {
            dispatch(
                showDirectErrorsAsync(
                    error as AxiosError,
                    'Не удалось отправить данные при редактировании кампании',
                ),
            );
        } finally {
            await dispatch(setDirectCampaignSaving(false));
        }
    };
}

export function updateDirectCampaignStateAsync(
    companyId: number,
    businessId: string,
    status: DirectProcessText,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const state = getState();
        const isLoading = selectDirectCampaignsLoading(state);

        if (isLoading) {
            return;
        }

        try {
            await dispatch(setDirectCampaignsLoading(true));
            await directService.updateDirectCampaignState(
                companyId,
                businessId,
                actionStatusMap[status],
            );
            await dispatch(setDirectCampaignsLoading(false));

            await dispatch(loadDirectCampaignsAsync(businessId));

            dispatch(showNotificationAsync(notificationStatusMap[status]));
        } catch (error) {
            dispatch(showDirectErrorsAsync(error as AxiosError, 'Не удалось обновить кампанию'));
        }

        dispatch(setDirectCampaignsLoading(false));
    };
}

export function loadDirectLoginsAsync(): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const state = getState();
        const isLoading = selectDirectLoginsLoading(state);

        if (isLoading) {
            return;
        }

        dispatch(setDirectLoginsLoading(true));

        try {
            const logins = await directService.loadDirectLogins();

            dispatch(setDirectLogins(logins));
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось загрузить данные о логинах пользователя'));
            dispatch(setDirectLogins([]));
        } finally {
            dispatch(setDirectLoginsLoading(false));
        }
    };
}
